<script lang="ts">
  // The "map disk", fast-travel overlay. Lists every known area grouped by
  // district (Domino City / Duel Academy) and warps the player on click. Styled
  // with the original map_switch disk art over the VRAINS panel primitive.
  import DataWindow from "./ui/DataWindow.svelte";
  import { MAP_DIRECTORY } from "../world/mapGraph.ts";
  import { asset } from "../../data.ts";
  import { onEscape } from "../hotkeys.ts";
  import { draggableDialog } from "../actions/draggableDialog.ts";

  let {
    open = false,
    current = "",
    onWarp,
    onClose,
  }: {
    open?: boolean;
    current?: string;
    onWarp: (mapId: string) => void;
    onClose: () => void;
  } = $props();

  function pick(id: string): void {
    if (id !== current) onWarp(id);
    onClose();
  }

  // Always mounted, so gate on `open` (only the disk closes on Escape).
  onEscape(() => onClose(), () => ({ enabled: open }));

  // Move focus into the dialog when it opens, so keyboard users don't keep
  // tabbing through the world behind it.
  function focusOnMount(node: HTMLElement): void {
    node.focus();
  }
</script>

{#if open}
  <!-- Dim backdrop; clicking it (but not the panel) closes the disk. -->
  <div
    class="mobile-dialog-host modal-scrim pointer-events-auto fixed inset-0 z-50 grid place-items-center bg-void/50"
    role="presentation"
    onclick={(e) => e.target === e.currentTarget && onClose()}
  >
    <div role="dialog" aria-modal="true" aria-label="Map disk" tabindex="-1" use:focusOnMount use:draggableDialog class="mobile-full-dialog outline-none">
      <DataWindow c={18} fill class="mobile-dialog-panel w-[min(92vw,720px)] p-5">
        <!-- Header -->
        <div data-dialog-drag-handle class="dialog-drag-handle mb-4 flex items-center gap-3">
          <img src={asset("maps/map_switch/mapdisk_case_01.png")} alt="" class="h-9 w-9 object-contain" />
          <h2 class="font-display text-lg font-bold uppercase tracking-[0.2em] text-cyan text-glow">
            Map Disk
          </h2>
          <span class="font-body text-xs text-ink-dim">- select a destination</span>
          <button
            onclick={onClose}
            title="Esc"
            class="chamfer-btn fx-sweep ml-auto bg-surface-2 px-3 py-1 font-display text-xs font-semibold uppercase tracking-widest text-ink-dim ring-1 ring-cyan-dim/25 transition-colors hover:text-cyan hover:ring-cyan/50"
            style="--c:6px"
          >
            ✕ Close
          </button>
        </div>

        <div class="mobile-dialog-scroll flex flex-col gap-4 overflow-y-auto scroll-cyber pr-1">
          {#each MAP_DIRECTORY as district (district.name)}
            <section>
              <div class="mb-2 flex items-center gap-2">
                <img src={asset(district.disk)} alt="" class="h-6 w-6 object-contain" />
                <!-- cyan, not gold: gold is reserved for currency (DESIGN.md) -->
                <h3 class="font-display text-sm font-bold uppercase tracking-[0.16em] text-cyan">
                  {district.name}
                </h3>
                <div class="ml-2 h-px flex-1 bg-cyan/20"></div>
              </div>
              <div class="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {#each district.maps as m (m.id)}
                  {@const here = m.id === current}
                  <button
                    onclick={() => pick(m.id)}
                    disabled={here}
                    class="chamfer-btn px-3 py-2 text-left font-display text-xs font-semibold uppercase tracking-wider transition-all {here
                      ? 'cursor-default bg-surface-2 text-cyan ring-1 ring-cyan/50'
                      : 'fx-sweep bg-void-2/70 text-ink ring-1 ring-cyan-dim/25 hover:bg-surface hover:text-cyan hover:ring-cyan/60'}"
                    style="--c:7px"
                  >
                    {m.label}
                    {#if here}<span class="ml-1 text-[10px] text-cyan/80">● here</span>{/if}
                  </button>
                {/each}
              </div>
            </section>
          {/each}
        </div>
      </DataWindow>
    </div>
  </div>
{/if}
